import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input'; 
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Link } from "react-router-dom";
import { Settings } from 'lucide-react';
import { Separator } from './ui/separator';
import { Copy, MessageCircle, ArrowLeft, Clock, DollarSign, Bot, User, LogIn } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth, SignInButton } from '@clerk/clerk-react';
import AuthButton from './AuthButton';
import { useStudioIQAPI } from '../services/studioiqAPI';
import SearchableClarification from './SearchableClarification';

const AIQuoteInterface = () => {
  const [customerName, setCustomerName] = useState('');
  const [jobId, setJobId] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [formattedQuote, setFormattedQuote] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentClarification, setCurrentClarification] = useState(null);
  const [clarificationFields, setClarificationFields] = useState(null);
  const [lastClarification, setLastClarification] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [originalPrompt, setOriginalPrompt] = useState('');
  const conversationEndRef = useRef(null);
  const quoteRef = useRef(null);
  const { toast } = useToast();
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const studioiqAPI = useStudioIQAPI(); // ✅ Keep this for potential future use
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const pollingRef = useRef(null);
  const isSignedInRef = useRef(isSignedIn);
  
  // Keep ref updated with latest isSignedIn value
  useEffect(() => {
    isSignedInRef.current = isSignedIn;
  }, [isSignedIn]);
  
  // Immediately stop polling and complete auth check if isSignedIn becomes true
  useEffect(() => {
    if (isLoaded && isSignedIn && !authCheckComplete) {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
      setAuthCheckComplete(true);
    }
  }, [isLoaded, isSignedIn, authCheckComplete]);
  
  // Wait for Clerk to sync auth state after cross-domain redirect
  // Use ref to always check latest isSignedIn value, not stale closure value
  useEffect(() => {
    if (!isLoaded) {
      setAuthCheckComplete(false);
      return;
    }

    // Clear any existing polling
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }

    // Check for redirect params or cross-domain referrer
    const hasRedirectParams = 
      window.location.search.includes('__clerk') || 
      window.location.hash.includes('__clerk');
    
    // Also check if referrer is from different domain
    let hasCrossDomainReferrer = false;
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        hasCrossDomainReferrer = referrerUrl.hostname !== window.location.hostname;
      } catch (e) {
        // Invalid referrer URL, ignore
      }
    }
    
    const isFromRedirect = hasRedirectParams || hasCrossDomainReferrer;
    
    if (isFromRedirect) {
      // Coming from redirect - poll for auth state changes
      // Use longer polling time since Clerk needs to sync cross-domain
      let checkCount = 0;
      const maxChecks = 60; // 6 seconds total (60 * 100ms)
      
      const pollAuthStatus = () => {
        checkCount++;
        
        // Always check the latest value from ref, not stale closure
        const currentIsSignedIn = isSignedInRef.current;
        
        
        // If user is signed in, we're done
        if (currentIsSignedIn) {
          setAuthCheckComplete(true);
          pollingRef.current = null;
          return;
        }
        
        // If we've checked enough times, give up
        if (checkCount >= maxChecks) {
          setAuthCheckComplete(true);
          pollingRef.current = null;
          return;
        }
        
        // Check again in 100ms
        pollingRef.current = setTimeout(pollAuthStatus, 100);
      };
      
      // Start polling after initial delay to allow Clerk to sync
      pollingRef.current = setTimeout(pollAuthStatus, 500);
    } else {
      // Normal page load - quick check
      pollingRef.current = setTimeout(() => {
        setAuthCheckComplete(true);
        pollingRef.current = null;
      }, 150);
    }

    // Cleanup
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [isLoaded]); // Only depend on isLoaded, not isSignedIn (we use ref for that)
  
  // Auto-generate job ID on mount
  useEffect(() => {
    const generateJobId = () => {
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 10000);
      return `JOB-${timestamp}-${random}`;
    };
    setJobId(generateJobId());
  }, []);

  // Auto-scroll to bottom when conversation history changes
  useEffect(() => {
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [conversationHistory]);

  // Monitor online/offline status and show messages
  useEffect(() => {
    const handleOnline = () => {
      toast({
        title: "Connection restored",
        description: "You're back online. You can now generate quotes.",
        variant: "default"
      });
    };

    const handleOffline = () => {
      toast({
        title: "No internet connection",
        description: "Please check your network connection and try again.",
        variant: "destructive"
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [toast]);

  const handleGenerateQuote = async () => {
    if (!isSignedIn) {
      toast({
        title: "Please sign in",
        description: "Authentication required to generate quotes",
        variant: "destructive"
      });
      return;
    }

    if (!chatInput.trim()) {
      toast({
        title: "Please describe the repair needed",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setCurrentClarification(null);
    
    try {
      // Store original prompt for potential re-parsing
      if (conversationHistory.length === 0) {
        setOriginalPrompt(chatInput.trim());
      }

      // Add user message to conversation history
      const userMessage = {
        role: 'user',
        content: chatInput.trim(),
        timestamp: new Date().toISOString()
      };

      const updatedHistory = [...conversationHistory, userMessage];
      setConversationHistory(updatedHistory);

      // Build complete inputText with ALL clarification answers for cache key
      const inputToSend = buildCompleteInputText(chatInput.trim(), conversationHistory);

      // Prepare customer context
      const customerContext = {
        customerName: customerName.trim() || 'Customer',
        jobId: jobId.trim()
      };


      // Use auth-check as the single entry point (it should handle internal routing)
      const token = await getToken({ template: 'studioiq-worker' });
      
      // const response = await fetch('https://auth-check.studioiq.workers.dev/', {
      const response = await fetch(`${process.env.REACT_APP_WORKER_BASE_URL}/`, {
      // const response = await fetch('http://localhost:8786/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          inputText: inputToSend,
          conversationHistory: updatedHistory,
          customerContext: customerContext
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));
        if (response.status === 429) {
          throw new Error(`${error.error}. You've used ${error.usage}/${error.limit} ${error.plan} plan messages this month.`);
        }
        throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Handle the response based on what auth-check returns
      if (result.clarificationText) {
        // Ruby needs clarification - show as chat message
        const rubyMessage = {
          role: 'ruby',
          content: result.clarificationText,
          timestamp: new Date().toISOString(),
          lastClarification: result.lastClarification  // Store for validation
        };

        setConversationHistory([...updatedHistory, rubyMessage]);
        setCurrentClarification(result.clarificationText);
        setClarificationFields(result.clarificationFields || null);
        setLastClarification(result.lastClarification || null);
        
        // Handle validation error (invalid answer)
        if (result.validationError) {
          setValidationError({
            message: result.clarificationText,
            invalidValue: result.invalidValue
          });
          toast({
            title: "Invalid answer",
            description: `"${result.invalidValue}" is not a valid option. Please select from the list.`,
            variant: "destructive"
          });
        } else {
          setValidationError(null);
          toast({
            title: "Ruby needs more information",
            description: "Please provide the requested details"
          });
        }
        
        setChatInput('');
        
      } else if (result.formattedQuote) {
        // Complete quote returned from full pipeline
        // Log database row details when quote is generated
        if (result.databaseRow) {
          console.log('📊 Database Row Used for Quote:', result.databaseRow);
        }
        
        setFormattedQuote(result.formattedQuote);
        setChatInput('');
        setCurrentClarification(null);
        setValidationError(null); // Clear validation error when quote is generated
        setClarificationFields(null);
        setLastClarification(null);
        
        // Scroll to quote section after a brief delay to ensure DOM is updated
        setTimeout(() => {
          if (quoteRef.current) {
            quoteRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 100);
        
        toast({
          title: "Quote generated successfully",  
          description: "Your quote is ready"
        });
        
      } else {
        throw new Error(`Unexpected response structure. Got keys: ${Object.keys(result || {}).join(', ')}`);
      }

    } catch (error) {
      toast({
        title: "Quote generation failed",
        description: error.message || "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyQuote = () => {
    if (!formattedQuote) return;
    
    const quoteText = `StudioIQ Quote\n\n${formattedQuote}`;
    
    navigator.clipboard.writeText(quoteText);
    toast({
      title: "Quote copied to clipboard",
      description: "Ready to share with customer"
    });
  };

  // Helper function to build complete inputText with all clarification answers
  const buildCompleteInputText = (currentInput, history) => {
    if (!history || history.length === 0) {
      return currentInput.trim();
    }

    // Extract all user messages from conversation history
    const userMessages = history.filter(msg => msg.role === 'user');
    
    if (userMessages.length === 0) {
      // No previous user messages, use original prompt or current input
      return originalPrompt || currentInput.trim();
    }

    // First user message is the original prompt
    const firstUserMessage = userMessages[0].content.trim();
    let completeInput = originalPrompt || firstUserMessage;
    
    // All subsequent user messages are clarification answers
    const clarificationAnswers = userMessages.slice(1).map(msg => msg.content.trim());
    
    // Append all previous clarification answers
    clarificationAnswers.forEach(answer => {
      if (answer.length > 0) {
        completeInput += `\n\nAdditional clarification: ${answer}`;
      }
    });
    
    // Append the current input as the latest clarification
    if (currentInput.trim() && currentInput.trim() !== firstUserMessage) {
      completeInput += `\n\nAdditional clarification: ${currentInput.trim()}`;
    }

    return completeInput;
  };

  const resetConversation = () => {
    setConversationHistory([]);
    setCurrentClarification(null);
    setClarificationFields(null);
    setLastClarification(null);
    setValidationError(null);
    setOriginalPrompt('');
    setFormattedQuote(null);
    setChatInput('');
  };

  const handleSearchableClarification = async (clarificationMessage) => {
    setIsLoading(true);
    
    try {
      // Add user response to conversation history
      const userMessage = {
        role: 'user',
        content: clarificationMessage,
        timestamp: new Date().toISOString()
      };
      
      const updatedHistory = [...conversationHistory, userMessage];
      setConversationHistory(updatedHistory);
      
      // Build complete inputText with ALL clarification answers for cache key
      const inputToSend = buildCompleteInputText(clarificationMessage, conversationHistory);
      
      const customerContext = {
        customerName: customerName.trim() || 'Customer',
        jobId: jobId.trim()
      };
      
      const token = await getToken({ template: 'studioiq-worker' });
      
      const response = await fetch(`${process.env.REACT_APP_WORKER_BASE_URL}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          inputText: inputToSend,
          conversationHistory: updatedHistory,
          customerContext: customerContext
        })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.clarificationText) {
        const rubyMessage = {
          role: 'ruby',
          content: result.clarificationText,
          timestamp: new Date().toISOString()
        };
        setConversationHistory([...updatedHistory, rubyMessage]);
        setCurrentClarification(result.clarificationText);
        setClarificationFields(result.clarificationFields || null);
      } else if (result.formattedQuote) {
        setFormattedQuote(result.formattedQuote);
        setCurrentClarification(null);
        setClarificationFields(null);
        setValidationError(null); // Clear validation error when quote is generated
        setLastClarification(null);
        
        // Scroll to quote section after a brief delay to ensure DOM is updated
        setTimeout(() => {
          if (quoteRef.current) {
            quoteRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 100);
        
        toast({
          title: "Quote generated successfully",
          description: "Your quote is ready"
        });
      }
      
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-bold text-slate-900">StudioIQ</h1>
            <Badge variant="outline" className="text-xs">
              AI Mode
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            {/* Settings Button - Only show when signed in */}
            {isSignedIn && (
              <Button asChild variant="default" size="sm">
                <Link to="/dashboard">
                  <Settings className="h-4 w-4 mr-2" />
                  Dashboard
                </Link>
              </Button>
            )}
            <AuthButton />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        
        {/* Customer Info */}
        <Card className="border-none shadow-lg">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Customer Name
                </label>
                <Input
                  placeholder="Enter customer name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="border-slate-200 focus:border-slate-400 transition-colors"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Job ID
                </label>
                <Input
                  placeholder="Auto-generated or custom"
                  value={jobId}
                  onChange={(e) => setJobId(e.target.value)}
                  className="border-slate-200 focus:border-slate-400 transition-colors"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Helpful Tips */}
        <Card className="border-l-4 border-l-blue-500 bg-blue-50 border-none shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <MessageCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-blue-900">
                  Ruby AI Assistant
                </p>
                <p className="text-xs text-blue-800">
                  Describe your repair in natural language. Ruby will ask clarifying questions if needed and generate an accurate quote.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Authentication Check */}
        {!authCheckComplete ? (
          <Card className="border-none shadow-lg">
            <CardContent className="p-6 text-center">
              <div className="space-y-4">
                <div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-600 rounded-full mx-auto"></div>
                <p className="text-slate-600">Checking authentication...</p>
              </div>
            </CardContent>
          </Card>
        ) : !isSignedIn ? (
          <Card className="border-none shadow-lg">
            <CardContent className="p-6 text-center">
              <div className="space-y-4">
                <LogIn className="h-12 w-12 text-slate-400 mx-auto" />
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Sign In Required</h3>
                  <p className="text-slate-600 mt-2">Please sign in to generate AI quotes</p>
                </div>
                <SignInButton mode="redirect" redirectUrl={window.location.href}>
                  <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                    Sign In to Continue
                  </Button>
                </SignInButton>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Conversation History */}
            {conversationHistory.length > 0 && (
              <Card className="border-none shadow-lg">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold text-slate-800">
                      Conversation
                    </CardTitle>
                    <Button 
                      onClick={resetConversation}
                      variant="outline"
                      size="sm"
                      className="text-xs"
                    >
                      Start Over
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-60 overflow-y-auto bg-slate-50 p-4 rounded-lg">
                    {conversationHistory.map((message, index) => (
                      <div 
                        key={index} 
                        className={`flex items-start space-x-3 ${
                          message.role === 'user' ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        {message.role === 'ruby' && (
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                            <Bot className="h-4 w-4 text-white" />
                          </div>
                        )}
                        <div 
                          className={`max-w-xs lg:max-w-md px-3 py-2 rounded-lg ${
                            message.role === 'user' 
                              ? 'bg-slate-900 text-white' 
                              : 'bg-white border border-slate-200'
                          }`}
                        >
                          <p className="text-sm">{message.content}</p>
                          <p className="text-xs opacity-60 mt-1">
                            {new Date(message.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                        {message.role === 'user' && (
                          <div className="flex-shrink-0 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                            <User className="h-4 w-4 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                    <div ref={conversationEndRef} />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Conditional Rendering: Only show input form if quote is NOT generated */}
            {!formattedQuote && (
              <>
                {/* Conditional Rendering: Searchable Clarification OR Regular Chat Input */}
                {clarificationFields && clarificationFields.length > 0 ? (
                  // Show searchable dropdown component
                  <SearchableClarification
                    clarificationText={currentClarification}
                    clarificationFields={clarificationFields}
                    onSubmit={handleSearchableClarification}
                    isLoading={isLoading}
                  />
                ) : (
                  // Show regular chat input
                  <Card className="border-none shadow-lg">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-slate-800">
                    {currentClarification ? 'Ruby needs more information' : 'Describe the Repair'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Validation Error Display */}
                  {validationError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-red-800">Invalid Answer</h3>
                          <p className="text-sm text-red-700 mt-1">
                            "{validationError.invalidValue}" is not a valid option. Please select from the list above.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <Textarea
                    placeholder={
                      currentClarification 
                        ? "Please provide the information Ruby requested above..."
                        : "Describe the item and repair needed... e.g., 'Resize 14k white gold ring from size 6 to 8, 4mm band, 2 stones' or 'Fix broken prong on platinum engagement ring'"
                    }
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    className={`min-h-[120px] transition-colors resize-none ${
                      validationError 
                        ? 'border-red-300 focus:border-red-500 bg-red-50' 
                        : 'border-slate-200 focus:border-slate-400'
                    }`}
                  />
                  <Button 
                    onClick={handleGenerateQuote}
                    disabled={isLoading || !chatInput.trim()}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-6 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>
                          {currentClarification ? 'Processing Response...' : 'Generating Quote...'}
                        </span>
                      </div>
                    ) : (
                      currentClarification ? 'Send Response' : 'Generate AI Quote'
                    )}
                  </Button>
                </CardContent>
              </Card>
                )}
              </>
            )}
          </>
        )}

        {/* Quote Output */}
        {formattedQuote && (
          <Card ref={quoteRef} className="border-none shadow-lg bg-gradient-to-r from-green-50 to-emerald-50">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-slate-800">
                  Quote Generated Successfully
                </CardTitle>
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                  Ready
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Success Message */}
              <div className="bg-green-100 border border-green-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-green-800">Quote Ready</h3>
                    <p className="text-sm text-green-700 mt-1">
                      Your quote has been generated successfully. Review the details below and copy to share with your customer.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardContent className="space-y-6">
              {/* Formatted Quote Display */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <pre className="whitespace-pre-wrap text-sm text-slate-900 font-mono leading-relaxed">
                  {formattedQuote}
                </pre>
              </div>

              {/* Copy Quote Button */}
              <Button 
                onClick={handleCopyQuote}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-6 rounded-lg transition-colors"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Quote
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Manual Mode Link */}
        <div className="text-center py-4">
          <Button 
            variant="ghost" 
            className="text-slate-600 hover:text-slate-800 hover:bg-slate-100 transition-colors"
            onClick={() => window.location.href = '/manual'}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Switch to Manual Mode
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIQuoteInterface;